import { scheduleMacrotask, useManagerEntity } from "@/shared/managerHelpers"
import { isBrowser } from "@/sharedTypes/browserUtils";
import { useEffect, useRef, useState } from "react"

const usePrevious = <T,>(value: T): T | null =>  {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}

export const useAsyncManagerState = <T,>(mngPath: string): [T | null, (newVal: T | null) => void, boolean] => {
  const getProperty = (path: string, obj?: any): any => {
    if (typeof window == 'undefined') {
      return null;
    }

    const prevObj = obj ?? window.manager
    return path.split('.').reduce((acc, key) => acc?.[key], prevObj);
  };

  const setProperty = (path: string, value: any, obj?: any): void => {
    if (typeof window == 'undefined') {
      return
    }

    const prevObj = obj ?? window.manager; 
    const keys = path.split('.');
    const lastKey = keys.pop()!;
    let current = prevObj;

    for (const key of keys) {
      if (typeof current[key] !== 'object' || current[key] === null) {
        current[key] = {};
      }
      current = current[key];
    }

    current[lastKey] = value;
  };

  const manager = useManagerEntity()
  const loading = Boolean(!manager);
  const [localState, setLocalState] = useState<T | null>(getProperty(mngPath) ?? null)
  const prevVal = usePrevious(localState)
  
  const setState = (newVal: T | null) => {
    setLocalState(newVal)
    scheduleMacrotask(() => {
      setProperty(mngPath, newVal)
      const newPropertyVal = getProperty(mngPath)
      if (newPropertyVal === prevVal && newVal !== newPropertyVal) {
        setLocalState(prevVal)
      }
    });
  }

  if (!isBrowser()) {
    return [null, () => {}, true]
  }

  return [localState, setState, loading]
}